import { useMemo } from 'react';
import _ from 'lodash';

// Event 계산 Helper Functions
function calculateCrossEventInfo(event, regularItems, freeItems) {
  const groupTotalQuantity = regularItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const groupAllowedFreeItems = Math.floor(groupTotalQuantity / event.N) * event.M;
  const groupCurrentFreeItems = freeItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  return {
    ...event,
    totalQuantity: groupTotalQuantity,
    allowedFreeItems: groupAllowedFreeItems,
    currentFreeItems: groupCurrentFreeItems,
    remainingFreeItems: groupAllowedFreeItems - groupCurrentFreeItems,
    regularItems,
    freeItems
  };
}

function calculateNonCrossEventInfo(event, regularItems, freeItems) {
  const variantCalculations = calculateVariantInfo(regularItems, freeItems, event);
  const totalRegularQuantity = regularItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const maxTotalAllowedFreeItems = Math.floor(totalRegularQuantity / event.N) * event.M;
  const currentTotalFreeItems = freeItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  return {
    ...event,
    variantCalculations,
    totalRegularQuantity,
    maxTotalAllowedFreeItems,
    currentTotalFreeItems,
    regularItems,
    freeItems
  };
}

export function useGroupedItems(cartItems = [], eventItemTotalQuantity = []) {
  return useMemo(() => {
    const processedItemIds = new Set();

    // 1. 이벤트를 종료일과 업데이트 시간으로 정렬
    const sortedEvents = [...eventItemTotalQuantity].sort((a, b) => {
      const endDateDiff = new Date(b.end_date) - new Date(a.end_date);
      if (endDateDiff === 0) {
        return new Date(b.updated_at) - new Date(a.updated_at);
      }
      return endDateDiff;
    });

    // 2. 이벤트가 적용된 그룹들을 처리
    const eventGroups = sortedEvents.flatMap(event => {
      const eventItems = cartItems.filter(item => {
        if (processedItemIds.has(item.id)) return false;

        const isEventItem = event.products.some(p => p.id === item.variant?.product?.id);
        if (isEventItem) {
          processedItemIds.add(item.id);
          return true;
        }
        return false;
      });

      if (eventItems.length === 0) return [];

      const productGroups = _.groupBy(eventItems, 'title');

      return Object.entries(productGroups).map(([title, items]) => {
        const { regularItems, freeItems } = separateItems(items);
        const eventInfo = event.cross
          ? calculateCrossEventInfo(event, regularItems, freeItems)
          : calculateNonCrossEventInfo(event, regularItems, freeItems);

        // regularItems와 freeItems를 따로 정렬한 후 합치기
        const sortedRegularItems = _.sortBy(regularItems, [
          item => new Date(item.created_at).getTime()
        ]);

        const sortedFreeItems = _.sortBy(freeItems, [
          item => new Date(item.created_at).getTime()
        ]);
        return {
          title,
          // regular items를 먼저, free items를 나중에 배치
          items: [...sortedRegularItems, ...sortedFreeItems],
          eventInfo
        };
      });
    });

    // 3. 이벤트가 적용되지 않은 아이템들 처리
    const nonEventItems = cartItems.filter(item => !processedItemIds.has(item.id));
    const nonEventGroups = Object.entries(_.groupBy(nonEventItems, 'title'))
    .map(([title, items]) => {
      const { regularItems, freeItems } = separateItems(items);
      
      const sortedRegularItems = _.sortBy(regularItems, [
        item => new Date(item.created_at).getTime()
      ]);
      
      const sortedFreeItems = _.sortBy(freeItems, [
        item => new Date(item.created_at).getTime()
      ]);
  
      return {
        title,
        items: [...sortedRegularItems, ...sortedFreeItems],
        eventInfo: null
      };
    });

    // 4. 최종 정렬 및 반환
    return sortGroups([...eventGroups, ...nonEventGroups]);
  }, [cartItems, eventItemTotalQuantity]);
}

function separateItems(items) {
  return {
    regularItems: items.filter(item => !item.free),
    freeItems: items.filter(item => item.free)
  };
}

function calculateVariantInfo(regularItems, freeItems, event) {
  return regularItems.reduce((acc, item) => {
    const variantQuantity = item.quantity;
    const allowedFreeItems = Math.floor(variantQuantity / event.N) * event.M;
    const variantFreeItems = freeItems.filter(
      f => f.variant_id === item.variant_id
    );
    const currentFreeItems = variantFreeItems.reduce(
      (sum, f) => sum + f.quantity,
      0
    );

    acc[item.variant_id] = {
      variantId: item.variant_id,
      productId: item.variant.product_id,
      quantity: variantQuantity,
      allowedFreeItems,
      currentFreeItems,
      remainingFreeItems: allowedFreeItems - currentFreeItems,
    };
    return acc;
  }, {});
}

function sortGroups(groups) {
  const getGroupSortKey = (items) => {
    const regularItem = items.find(item => !item.free);
    if (!regularItem) return '';
    return `${regularItem.variant?.product?.id}_${regularItem.title}`;
  };

  return groups.sort((a, b) => {
    // 이벤트가 있는 그룹을 우선 정렬
    if (a.eventInfo && !b.eventInfo) return -1;
    if (!a.eventInfo && b.eventInfo) return 1;

    // 둘 다 이벤트가 있는 경우, 이벤트의 endDate와 updatedAt 기준으로 정렬
    if (a.eventInfo && b.eventInfo) {
      const endDateDiff = new Date(b.eventInfo.end_date) - new Date(a.eventInfo.end_date);
      if (endDateDiff === 0) {
        return new Date(b.eventInfo.updated_at) - new Date(a.eventInfo.updated_at);
      }
      return endDateDiff;
    }

    // 이벤트가 없는 경우 기존 정렬 로직 사용
    const keyA = getGroupSortKey(a.items);
    const keyB = getGroupSortKey(b.items);
    return keyA.localeCompare(keyB);
  });
}